import { useQueryClient } from '@tanstack/react-query'
import { MessagesSquare } from 'lucide-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import type { TenantResponse } from '@/api/responses'
import { describeApiError } from '@/api/problemDetails'
import { Alert, Button, Spinner } from '@/components/ui'
import { tenantsQueryKey } from '@/features/tenants/queries'

import { ChannelPairingCard } from '../components/ChannelPairingCard'
import { ProvisioningProgress } from '../components/ProvisioningProgress'
import { useProvisioningStatus, useStartProvisioning } from '../useProvisioning'
import {
  buildMilestones,
  deriveChannelId,
  isProvisioningComplete,
  isProvisioningFailed,
  isProvisioningNotStarted,
  shouldShowPairing,
} from '../wizardSteps'

export interface ConnectWhatsAppStepProps {
  readonly tenant: TenantResponse
}

/**
 * Passo "Conectar WhatsApp": agora uma acao EXPLICITA do usuario (sem auto-fire). Depois de
 * experimentar o agente no Playground, o usuario decide colocar o atendimento no ar clicando em
 * "Conectar WhatsApp" — so entao a saga de provisioning inicia. A partir dai, acompanha o status
 * por polling e mostra o QR quando o canal fica aguardando pareamento. Todo o estado vem do backend,
 * entao fechar/reabrir o browser sempre volta ao ponto certo.
 */
export function ConnectWhatsAppStep({ tenant }: ConnectWhatsAppStepProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const statusQuery = useProvisioningStatus(tenant.id)
  const startMutation = useStartProvisioning(tenant.id)

  const status = statusQuery.data

  // Quando a saga conclui, `tenant.status` vira `Active` no backend (MarkReadyStep): invalida a
  // lista de tenants para que a wizard avance para o passo "Pronto".
  useEffect(() => {
    if (isProvisioningComplete(status)) {
      void queryClient.invalidateQueries({ queryKey: tenantsQueryKey })
    }
  }, [status, queryClient])

  function handleStart(): void {
    startMutation.mutate()
  }

  if (statusQuery.isPending) {
    return (
      <div className="flex min-h-[420px] items-center justify-center p-10">
        <Spinner label={t('loading')} />
      </div>
    )
  }

  if (statusQuery.isError) {
    const [message] = describeApiError(statusQuery.error)

    return (
      <div className="flex min-h-[420px] flex-col items-center justify-center gap-4 p-10 text-center">
        <Alert variant="danger" title={t('error')}>
          {message}
        </Alert>
        <Button
          onClick={() => {
            void statusQuery.refetch()
          }}
        >
          {t('retry')}
        </Button>
      </div>
    )
  }

  const notStarted = isProvisioningNotStarted(status)
  const milestones = buildMilestones(status)
  const failed = isProvisioningFailed(status)
  const showPairing = shouldShowPairing(status)
  const channelId = deriveChannelId(status)

  // Estado inicial: conectar o WhatsApp e uma escolha do usuario (empty state = action state).
  if (notStarted && !failed) {
    return (
      <div className="flex flex-col items-center gap-6 p-6 text-center sm:p-10">
        <span
          aria-hidden="true"
          className="flex h-13 w-13 items-center justify-center rounded-2xl bg-status-success-subtle text-status-success"
        >
          <MessagesSquare className="h-6 w-6" />
        </span>
        <div className="flex max-w-xl flex-col gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-action-primary">
            {t('onboarding.connect.eyebrow')}
          </span>
          <h2 className="text-2xl font-semibold tracking-tight text-foreground-primary sm:text-3xl">
            {t('onboarding.connect.title')}
          </h2>
          <p className="text-base leading-relaxed text-foreground-secondary">
            {t('onboarding.connect.startDescription')}
          </p>
        </div>

        <div className="flex w-full max-w-xl gap-2.5 rounded-lg border border-status-info/25 bg-status-info-subtle p-3 text-left text-xs leading-relaxed text-status-info">
          <span aria-hidden="true">&#9432;</span>
          <span>{t('onboarding.connect.dedicatedNumberNotice')}</span>
        </div>

        <Button onClick={handleStart} loading={startMutation.isPending}>
          {t('onboarding.connect.startCta')}
        </Button>
      </div>
    )
  }

  return (
    <div className="grid gap-10 p-6 sm:grid-cols-2 sm:p-10">
      <div className="flex flex-col gap-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-action-primary">
          {t('onboarding.connect.eyebrow')}
        </span>
        <h2 className="text-2xl font-semibold tracking-tight text-foreground-primary sm:text-3xl">
          {t('onboarding.connect.title')}
        </h2>
        <p className="text-sm leading-relaxed text-foreground-secondary">
          {t('onboarding.connect.description')}
        </p>
        {showPairing ? (
          <>
            <ol className="flex flex-col gap-2.5 pl-4 text-sm leading-relaxed text-foreground-secondary marker:text-foreground-muted list-decimal">
              <li>{t('onboarding.connect.instructions1')}</li>
              <li>{t('onboarding.connect.instructions2')}</li>
              <li>{t('onboarding.connect.instructions3')}</li>
            </ol>
            <div className="flex gap-2.5 rounded-lg border border-status-info/25 bg-status-info-subtle p-3 text-xs leading-relaxed text-status-info">
              <span aria-hidden="true">&#9432;</span>
              <span>{t('onboarding.connect.dedicatedNumberNotice')}</span>
            </div>
          </>
        ) : null}
      </div>

      <div className="flex flex-col gap-4">
        {failed ? (
          <Alert variant="danger" title={t('error')}>
            <div className="flex flex-col gap-2">
              {status?.lastErrorCode !== null && status?.lastErrorCode !== undefined ? (
                <p>
                  {t('onboarding.connect.errorDetails', {
                    code: status.lastErrorCode,
                    correlationId: status.correlationId ?? '',
                  })}
                </p>
              ) : null}
              <Button
                size="sm"
                onClick={handleStart}
                loading={startMutation.isPending}
                className="self-start"
              >
                {t('retry')}
              </Button>
            </div>
          </Alert>
        ) : null}

        {showPairing ? (
          <ChannelPairingCard tenantId={tenant.id} channelId={channelId} />
        ) : (
          <ProvisioningProgress milestones={milestones} />
        )}
      </div>
    </div>
  )
}
